import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  PaperPlaneTilt,
  CheckCircle,
  WarningCircle,
  CaretDown,
} from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const STEPS = [
  [
    "Register the app",
    "Sign in at entra.microsoft.com as a Microsoft 365 admin, then go to Entra ID → App registrations → New registration. Name it 'Brioni Granite Mail', choose 'Accounts in this organizational directory only', leave the redirect URI empty and press Register.",
  ],
  [
    "Copy the two IDs",
    "On the Overview page copy the Application (client) ID and the Directory (tenant) ID into the boxes below.",
  ],
  [
    "Add permission to send mail",
    "API permissions → Add a permission → Microsoft Graph → Application permissions → search Mail.Send → tick it → Add permissions.",
  ],
  [
    "Grant admin consent",
    "Still on API permissions, press 'Grant admin consent for <your company>' and confirm. The row must then say 'Granted'.",
  ],
  [
    "Create the secret",
    "Certificates & secrets → Client secrets → New client secret. Give it a name and an expiry, press Add, then copy the VALUE column immediately (not the Secret ID) — Microsoft only shows it once. Paste it below.",
  ],
  [
    "Pick the mailbox to send from",
    "Type the shared mailbox you want the emails to come from, for example info@brionigranite.com. It must be a real Microsoft 365 mailbox in the same tenant.",
  ],
  [
    "Optional but recommended — lock the app to that one mailbox",
    'Ask your IT person to run this in Exchange Online PowerShell so the app can only ever send from that mailbox:\nNew-ApplicationAccessPolicy -AppId <your client id> -PolicyScopeGroupId <mail-enabled security group with the mailbox> -AccessRight RestrictAccess -Description "Brioni Granite office app"',
  ],
];

const Field = ({ label, hint, ...props }) => (
  <label className="block">
    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
      {label}
    </span>
    <Input {...props} className="mt-1 h-11 border-zinc-800 bg-zinc-950" />
    {hint && <p className="mt-1 text-xs text-zinc-600">{hint}</p>}
  </label>
);

export default function MicrosoftMailSettings() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [conf, setConf] = useState(null);
  const [secret, setSecret] = useState("");
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [openSteps, setOpenSteps] = useState(true);
  const latest = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/settings/microsoft-mail");
    latest.current = data;
    setConf(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (user?.email) setTestTo((cur) => cur || user.email);
  }, [user]);

  const set = (key, value) =>
    setConf((prev) => {
      const next = { ...prev, [key]: value };
      latest.current = next;
      return next;
    });

  const save = async (override = {}) => {
    setBusy(true);
    try {
      const body = { ...latest.current, ...override };
      const { data } = await api.put("/settings/microsoft-mail", {
        tenant_id: body.tenant_id || "",
        client_id: body.client_id || "",
        client_secret: secret,
        mailbox: body.mailbox || "",
        display_name: body.display_name || "",
        reply_to: body.reply_to || "",
        enabled: !!body.enabled,
      });
      latest.current = data;
      setConf(data);
      setSecret("");
      toast.success("Microsoft 365 settings saved");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save those settings",
      );
    }
    setBusy(false);
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/settings/microsoft-mail/test", {
        to: testTo,
      });
      toast.success(data.message);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "The test email did not go through",
      );
    }
    await load();
    setBusy(false);
  };

  const disconnect = async () => {
    if (
      !window.confirm(
        "Disconnect Microsoft 365? Email goes back to the built-in sender.",
      )
    )
      return;
    const { data } = await api.delete("/settings/microsoft-mail");
    latest.current = data;
    setConf(data);
    setSecret("");
    toast.success("Microsoft 365 disconnected");
  };

  if (!conf)
    return (
      <div className="grain min-h-screen">
        <AppHeader title="Microsoft 365 email" />
      </div>
    );

  const live = conf.configured && conf.enabled;

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Microsoft 365 email" />
      <main className="relative z-10 pb-36 pt-6">
        <button
          type="button"
          data-testid="ms-mail-back"
          onClick={() => nav("/settings")}
          className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-200"
        >
          <ArrowLeft size={13} weight="bold" /> Settings
        </button>
        <div className="mt-3 flex items-center gap-3">
          <PaperPlaneTilt size={22} weight="bold" className="text-indigo-300" />
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Microsoft 365 email
          </h1>
        </div>
        <p className="mt-1 max-w-2xl text-sm text-zinc-500">
          Send quotes, invoices, portal invites and reminders from your own
          Microsoft 365 mailbox, so replies come back to you and a copy sits in
          Sent Items. If Microsoft ever refuses a message the app falls back to
          the built-in sender, so nothing is lost.
        </p>

        <div className="mt-6 space-y-4" data-testid="ms-mail-settings">
          <section
            className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
            data-testid="ms-mail-status-card"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                {live ? (
                  <CheckCircle
                    size={20}
                    weight="bold"
                    className="mt-0.5 text-emerald-400"
                  />
                ) : (
                  <WarningCircle
                    size={20}
                    weight="bold"
                    className="mt-0.5 text-amber-400"
                  />
                )}
                <div>
                  <div
                    className="text-sm font-semibold text-zinc-100"
                    data-testid="ms-mail-status"
                  >
                    {live
                      ? "Sending through Microsoft 365"
                      : conf.configured
                        ? "Set up, but switched off"
                        : "Not set up yet"}
                  </div>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    {conf.mailbox
                      ? `From ${conf.mailbox}`
                      : "Add your Azure app details below and pick the mailbox to send from"}
                  </p>
                  {conf.last_test_at && (
                    <p
                      className={`mt-1.5 text-xs ${conf.last_test_ok ? "text-emerald-400" : "text-red-400"}`}
                      data-testid="ms-mail-last-test"
                    >
                      Last test: {conf.last_test_message}
                    </p>
                  )}
                  {conf.last_error && !conf.last_test_ok && (
                    <p className="mt-1 text-xs text-zinc-600">
                      Last send problem: {conf.last_error}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Use Microsoft 365
                </span>
                <Switch
                  data-testid="ms-mail-enabled"
                  checked={!!conf.enabled}
                  onCheckedChange={(v) => {
                    set("enabled", v);
                    save({ enabled: v });
                  }}
                />
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-zinc-800 bg-zinc-900 p-5">
            <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500">
              Your Azure app details
            </h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <Field
                label="Directory (tenant) ID"
                data-testid="ms-mail-tenant"
                value={conf.tenant_id || ""}
                onChange={(e) => set("tenant_id", e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
              />
              <Field
                label="Application (client) ID"
                data-testid="ms-mail-client"
                value={conf.client_id || ""}
                onChange={(e) => set("client_id", e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
              />
              <Field
                label="Client secret value"
                data-testid="ms-mail-secret"
                type="password"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder={
                  conf.has_secret
                    ? "•••••••• saved — type to replace"
                    : "Paste the secret VALUE"
                }
                hint="Stored encrypted on the server and never shown again."
              />
              <Field
                label="Send from mailbox"
                data-testid="ms-mail-mailbox"
                value={conf.mailbox || ""}
                onChange={(e) => set("mailbox", e.target.value)}
                placeholder="info@brionigranite.com"
                hint="A real Microsoft 365 mailbox — a shared one works well."
              />
              <Field
                label="Display name"
                data-testid="ms-mail-display-name"
                value={conf.display_name || ""}
                onChange={(e) => set("display_name", e.target.value)}
                placeholder="Brioni Granite"
              />
              <Field
                label="Reply-to (optional)"
                data-testid="ms-mail-reply-to"
                value={conf.reply_to || ""}
                onChange={(e) => set("reply_to", e.target.value)}
                placeholder="sales@brionigranite.com"
              />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                data-testid="ms-mail-save"
                disabled={busy}
                onClick={() => save()}
                className="h-11 w-full bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400 sm:w-auto"
              >
                {busy ? "Saving…" : "Save details"}
              </Button>
              {conf.configured && (
                <Button
                  data-testid="ms-mail-disconnect"
                  variant="outline"
                  onClick={disconnect}
                  className="h-11 w-full border-red-500/30 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest text-red-400 hover:bg-red-500/10 sm:ml-auto sm:w-auto"
                >
                  Disconnect
                </Button>
              )}
            </div>
          </section>

          <section className="rounded-lg border border-zinc-800 bg-zinc-900 p-5">
            <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500">
              Send a test email
            </h2>
            <p className="mt-1 text-xs text-zinc-500">
              Proves the connection before your customers see it.
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <Input
                data-testid="ms-mail-test-to"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="you@brionigranite.com"
                className="h-11 border-zinc-800 bg-zinc-950"
              />
              <Button
                data-testid="ms-mail-test-send"
                disabled={busy || !conf.configured}
                onClick={sendTest}
                className="h-11 shrink-0 bg-emerald-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-emerald-400"
              >
                <PaperPlaneTilt size={14} weight="bold" className="mr-2" />
                Send test email
              </Button>
            </div>
            <button
              type="button"
              data-testid="ms-mail-activity-link"
              onClick={() => nav("/settings/email-activity")}
              className="mt-3 text-xs font-semibold text-indigo-300 hover:text-indigo-200"
            >
              See every email that has gone out →
            </button>
          </section>

          <section className="rounded-lg border border-zinc-800 bg-zinc-900 p-5">
            <button
              type="button"
              data-testid="ms-mail-steps-toggle"
              onClick={() => setOpenSteps((v) => !v)}
              className="flex w-full items-center gap-2 text-left"
            >
              <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                How to get these details from Microsoft
              </h2>
              <CaretDown
                size={14}
                weight="bold"
                className={`ml-auto text-zinc-500 transition-transform duration-200 ${
                  openSteps ? "rotate-180" : ""
                }`}
              />
            </button>
            {openSteps && (
              <ol className="mt-4 space-y-3" data-testid="ms-mail-steps">
                {STEPS.map(([title, body], i) => (
                  <li key={title} className="flex gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-500/15 text-[11px] font-bold text-indigo-300">
                      {i + 1}
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-zinc-100">
                        {title}
                      </div>
                      <p className="mt-0.5 whitespace-pre-wrap break-words text-xs leading-relaxed text-zinc-500">
                        {body}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
